import React from 'react';
import { Col, Divider, Row } from 'antd';
import { isEqual, isNil } from 'lodash';

import HistoryField from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryField';
import HistoryLog from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryLog';
import {
  AssignmentHistoryDetailsModel,
  AssignmentHistoryResponse,
  AssignmentMultiRateEditModel,
} from 'api/adminApiClient';
import {
  getOvertimeLabel,
  getPayTypeLabel,
  calculateFinancialInfoHistory,
} from 'utils/assignmentUtils';
import { convertArraysToOneLength } from 'utils/arrayUtils';
import { formatNumberAsCurrency } from 'utils/numberUtils';
import { useAppSelector } from 'hooks/useAppSelector';
import { enhancedPayCodesSelector } from 'redux/selectors/assignmentSelectors';
import { EnhancedPayCodeDetail } from 'components/modals/AssignmentDataModal/FinancialInfo/interfaces';

import styles from '../../../HistoryCard.module.less';

const emptyPayCodeInfo: AssignmentMultiRateEditModel = {
  payType: undefined,
  payRate: undefined,
  billRate: undefined,
  specialInstructions: undefined,
  memberBilledIndicator: undefined,
};

interface EnhancedAssignmentMultiRate extends AssignmentMultiRateEditModel {
  payRateTypeName: string | null;
  payCodeName: string | null;
}

type Props = {
  historyId: string;
  historyDetails: AssignmentHistoryDetailsModel;
};

export interface FinancialInformationHistory {
  vmsRate: number;
  mspRate: number;
  mbpRate: number;
  mbpPortion: number;
  marginPercent: number;
  markupPercent: number;
}

export default function FinancialInformationDetailsCard(props: Props) {
  const { historyId, historyDetails } = props;
  const { oldData, newData } = historyDetails;
  const enhancedPayCodes = useAppSelector(enhancedPayCodesSelector);

  if (!oldData || !newData) return null;

  const enhanceMultiRates = (
    multiRates: AssignmentMultiRateEditModel[],
    enhancedPayCodes: EnhancedPayCodeDetail[],
  ): EnhancedAssignmentMultiRate[] => {
    return multiRates.map((rate) => {
      const payCode = enhancedPayCodes.find(
        (payCode) => payCode.id === rate.payCodeId,
      );
      return {
        ...rate,
        payRateTypeName: payCode?.payRateTypeName || null,
        payCodeName: payCode?.name || null,
      };
    });
  };

  //Multiple Rates
  const { oldData: oldMultiRates, newData: newMultiRates } =
    convertArraysToOneLength<AssignmentMultiRateEditModel>(
      oldData?.multiRates || [],
      newData?.multiRates || [],
      emptyPayCodeInfo,
    );

  const enhancedOldMultiRates = enhanceMultiRates(
    oldMultiRates,
    enhancedPayCodes,
  );

  const enhancedNewMultiRates = enhanceMultiRates(
    newMultiRates,
    enhancedPayCodes,
  );

  const financialInfoOldHistory = calculateFinancialInfoHistory(oldData);
  const financialInfoNewHistory = calculateFinancialInfoHistory(newData);
  const {
    vmsRate: vmsRateOld,
    mspRate: mspRateOld,
    mbpRate: mbpRateOld,
    mbpPortion: mbpPortionOld,
    marginPercent: marginPercentOld,
    markupPercent: markupPercentOld,
  } = financialInfoOldHistory;
  const {
    vmsRate: vmsRateNew,
    mspRate: mspRateNew,
    mbpRate: mbpRateNew,
    mbpPortion: mbpPortionNew,
    marginPercent: marginPercentNew,
    markupPercent: markupPercentNew,
  } = financialInfoNewHistory;

  // Rates calculator
  const hasMbpRateChanged = !isEqual(mbpRateOld || '', mbpRateNew || '');
  const hasMbpPortionChanged = !isEqual(
    mbpPortionOld || '',
    mbpPortionNew || '',
  );
  const hasRatesCalculatorChanged = hasMbpRateChanged || hasMbpPortionChanged;

  // Markup calculator
  const hasMarginPercentChanged = !isEqual(
    marginPercentOld || '',
    marginPercentNew || '',
  );
  const hasMarkupPercentChanged = !isEqual(
    markupPercentOld || '',
    markupPercentNew || '',
  );
  const hasMarkupCalculatorChanged =
    hasMarginPercentChanged || hasMarkupPercentChanged;

  //Financial information
  const hasPayTypeChanged = !isEqual(
    oldData?.financialInfo?.payType || '',
    newData?.financialInfo?.payType || '',
  );
  const hasPayRateChanged = !isEqual(
    oldData?.financialInfo?.payRate || '',
    newData?.financialInfo?.payRate || '',
  );
  const hasVmsRateChanged = !isEqual(vmsRateOld || '', vmsRateNew || '');
  const hasMspRateChanged = !isEqual(mspRateOld || '', mspRateNew || '');
  const hasBillRateChanged = !isEqual(
    oldData?.financialInfo?.billRate || '',
    newData?.financialInfo?.billRate || '',
  );
  const hasProfitRateChanged = !isEqual(
    oldData?.financialInfo?.profitRate || '',
    newData?.financialInfo?.profitRate || '',
  );
  const hasOvertimeChanged = !isEqual(
    oldData?.financialInfo?.overtime || '',
    newData?.financialInfo?.overtime || '',
  );
  const hasOvertimePayRateChanged = !isEqual(
    oldData?.financialInfo?.overtimePayRate || '',
    newData?.financialInfo?.overtimePayRate || '',
  );
  const hasOvertimeBillRateChanged = !isEqual(
    oldData?.financialInfo?.overtimeBillRate || '',
    newData?.financialInfo?.overtimeBillRate || '',
  );
  const hasDTBillRateChanged = !isEqual(
    oldData?.financialInfo?.dtBillRate || '',
    newData?.financialInfo?.dtBillRate || '',
  );
  const hasDTPayRateChanged = !isEqual(
    oldData?.financialInfo?.dtPayRate || '',
    newData?.financialInfo?.dtPayRate || '',
  );

  const areMultiRatesChanged = () => {
    const oldMultiRates = oldData?.multiRates || [];
    const newMultiRates = newData?.multiRates || [];
    if (oldMultiRates.length !== newMultiRates.length) return true;

    return newMultiRates.some((newRate: any, index: number) => {
      const oldRate: any = oldMultiRates[index];
      return Object.keys({ ...newRate, ...oldRate }).some(
        (key) => newRate[key] !== oldRate[key],
      );
    });
  };

  const hasMultiRatesChanged = areMultiRatesChanged();

  const hasBasicFinancialInformationChanged =
    hasPayTypeChanged ||
    hasPayRateChanged ||
    hasVmsRateChanged ||
    hasMspRateChanged ||
    hasBillRateChanged ||
    hasProfitRateChanged ||
    hasOvertimeChanged ||
    hasOvertimePayRateChanged ||
    hasOvertimeBillRateChanged ||
    hasDTBillRateChanged ||
    hasDTPayRateChanged;

  const hasFinancialInformationChanged =
    hasBasicFinancialInformationChanged || hasMultiRatesChanged;

  const compareMultiRateField = (
    oldRates1: EnhancedAssignmentMultiRate[],
    newRates1: EnhancedAssignmentMultiRate[],
    payCodeId: string | undefined,
    field: keyof EnhancedAssignmentMultiRate,
  ) => {
    const oldRate = enhancedOldMultiRates.find(
      (rate) => rate.payCodeId === payCodeId,
    );
    const newRate = enhancedNewMultiRates.find(
      (rate) => rate.payCodeId === payCodeId,
    );

    return !isEqual(
      !isNil(oldRate?.[field]) ? oldRate?.[field] : '',
      !isNil(newRate?.[field]) ? newRate?.[field] : '',
    );
  };

  const renderMultiRatesSection = (
    multiRates: EnhancedAssignmentMultiRate[],
  ) => {
    return (
      <>
        {multiRates.map((multiRate, index) => {
          const hasPayRateChanges = compareMultiRateField(
            enhancedOldMultiRates,
            enhancedNewMultiRates,
            multiRate.payCodeId,
            'payRate',
          );
          const hasBillRateChanges = compareMultiRateField(
            enhancedOldMultiRates,
            enhancedNewMultiRates,
            multiRate.payCodeId,
            'billRate',
          );
          const hasMemberBilledIndicatorChanges = compareMultiRateField(
            enhancedOldMultiRates,
            enhancedNewMultiRates,
            multiRate.payCodeId,
            'memberBilledIndicator',
          );
          const hasSpecialInstructionsChanges = compareMultiRateField(
            enhancedOldMultiRates,
            enhancedNewMultiRates,
            multiRate.payCodeId,
            'specialInstructions',
          );
          const hasNoOneChanges =
            !hasPayRateChanges &&
            !hasBillRateChanges &&
            !hasMemberBilledIndicatorChanges &&
            !hasSpecialInstructionsChanges;

          if (hasNoOneChanges && !isNil(multiRate.id)) return null;

          return (
            <React.Fragment key={index}>
              <HistoryField
                title="Pay rate type"
                value={multiRate.payRateTypeName}
              />

              <HistoryField title="Pay code" value={multiRate.payCodeName} />

              {(hasNoOneChanges || hasPayRateChanges) && (
                <HistoryField
                  title="Pay rate"
                  value={formatNumberAsCurrency({
                    num: multiRate?.payRate,
                  })}
                />
              )}
              {(hasNoOneChanges || hasBillRateChanges) && (
                <HistoryField
                  title="Bill rate"
                  value={formatNumberAsCurrency({
                    num: multiRate?.billRate,
                  })}
                />
              )}
              {(hasNoOneChanges || hasMemberBilledIndicatorChanges) && (
                <HistoryField
                  title="Paid by"
                  value={
                    !isNil(multiRate?.memberBilledIndicator)
                      ? multiRate?.memberBilledIndicator
                        ? 'Member'
                        : 'Customer'
                      : undefined
                  }
                />
              )}
              {(hasNoOneChanges || hasSpecialInstructionsChanges) && (
                <HistoryField
                  title="Special instructions"
                  value={multiRate?.specialInstructions}
                />
              )}
              <Col md={24} xs={24}>
                <div className={styles.separate} />
              </Col>
            </React.Fragment>
          );
        })}
      </>
    );
  };

  const renderFinancialInformationSection = (
    multiRates: EnhancedAssignmentMultiRate[],
    assignment?: AssignmentHistoryResponse,
  ) => {
    if (!assignment) return null;

    const { financialInfo } = assignment;

    const sectionName = 'financial-information';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Financial information</div>
          </Col>
          {hasPayTypeChanged && (
            <HistoryField
              title="Pay type"
              value={getPayTypeLabel(financialInfo?.payType)}
              dataId={`${sectionName}-pay-type-field`}
            />
          )}
          {hasPayRateChanged && (
            <HistoryField
              title="Base pay rate"
              value={formatNumberAsCurrency({
                num: financialInfo?.payRate,
              })}
              dataId={`${sectionName}-pay-rate-field`}
            />
          )}
          {hasBillRateChanged && (
            <HistoryField
              title="Bill rate"
              value={formatNumberAsCurrency({
                num: financialInfo?.billRate,
              })}
              dataId={`${sectionName}-bill-rate-field`}
            />
          )}
          {hasProfitRateChanged && (
            <HistoryField
              title="Profit rate"
              value={formatNumberAsCurrency({
                num: financialInfo?.profitRate,
              })}
              dataId={`${sectionName}-profit-rate-field`}
            />
          )}
          {hasMspRateChanged && (
            <HistoryField
              title="MSP Rate"
              value={financialInfo?.mspFee}
              dataId={`${sectionName}-msp-rate-field`}
            />
          )}
          {hasVmsRateChanged && (
            <HistoryField
              title="VMS Rate"
              value={financialInfo?.vmsFee}
              dataId={`${sectionName}-vms-rate-field`}
            />
          )}
          {hasOvertimeChanged && (
            <HistoryField
              title="Overtime"
              value={getOvertimeLabel(financialInfo?.overtime)}
              dataId={`${sectionName}-overtime-field`}
            />
          )}
          {hasOvertimePayRateChanged && (
            <HistoryField
              title="OT pay rate"
              value={formatNumberAsCurrency({
                num: financialInfo?.overtimePayRate,
              })}
              dataId={`${sectionName}-overtime-pay-rate-field`}
            />
          )}
          {hasOvertimeBillRateChanged && (
            <HistoryField
              title="OT bill rate"
              value={formatNumberAsCurrency({
                num: financialInfo?.overtimeBillRate,
              })}
              dataId={`${sectionName}-overtime-bill-rate-field`}
            />
          )}
          {hasDTBillRateChanged && (
            <HistoryField
              title="DT bill rate"
              value={formatNumberAsCurrency({ num: financialInfo?.dtBillRate })}
              dataId={`${sectionName}-dt-bill-rate-field`}
            />
          )}
          {hasDTPayRateChanged && (
            <HistoryField
              title="DT pay rate"
              value={formatNumberAsCurrency({ num: financialInfo?.dtPayRate })}
              dataId={`${sectionName}-dt-pay-rate-field`}
            />
          )}
        </Row>

        {hasMultiRatesChanged && (
          <Row gutter={[10, 10]}>
            {hasBasicFinancialInformationChanged && (
              <Col xs={24}>
                <div className={styles.separate} />
              </Col>
            )}
            {renderMultiRatesSection(multiRates)}
          </Row>
        )}
      </>
    );
  };

  const renderRatesCalculatorSection = (
    financialInfo?: FinancialInformationHistory,
  ) => {
    if (!financialInfo) return null;

    const sectionName = 'rates-calculator';
    return (
      <Row gutter={[10, 10]}>
        <Col xs={24}>
          <div className={styles.cardTitle}>Rates calculator</div>
        </Col>
        {hasMbpRateChanged && (
          <HistoryField
            title="MBP Rate"
            value={financialInfo?.mbpRate}
            dataId={`${sectionName}-mbp-rate-field`}
          />
        )}
        {hasMbpPortionChanged && (
          <HistoryField
            title="MBP Portion"
            value={formatNumberAsCurrency({ num: financialInfo?.mbpPortion })}
            dataId={`${sectionName}-mbp-portion-field`}
          />
        )}

        <Divider />
      </Row>
    );
  };

  const renderMarkupCalculator = (
    financialInfo?: FinancialInformationHistory,
  ) => {
    if (!financialInfo) return null;

    const sectionName = 'markup-calculator';
    return (
      <Row gutter={[10, 10]}>
        <Col xs={24}>
          <div className={styles.cardTitle}>Markup calculator</div>
        </Col>

        {hasMarginPercentChanged && (
          <HistoryField
            title="Margin %"
            value={formatNumberAsCurrency({
              num: financialInfo?.marginPercent,
              isRightSymbol: false,
              symbol: '%',
            })}
            dataId={`${sectionName}-margin-percent-field`}
          />
        )}
        {hasMarkupPercentChanged && (
          <HistoryField
            title="Markup %"
            value={formatNumberAsCurrency({
              num: financialInfo?.markupPercent,
              isRightSymbol: false,
              symbol: '%',
            })}
            dataId={`${sectionName}-markup-percent-field`}
          />
        )}

        <Divider />
      </Row>
    );
  };

  const renderFinancialInformation = (
    multiRates: EnhancedAssignmentMultiRate[],
    assignment?: AssignmentHistoryResponse,
    financialInfo?: FinancialInformationHistory,
  ) => {
    return (
      <>
        {hasFinancialInformationChanged &&
          renderFinancialInformationSection(multiRates, assignment)}
        {hasRatesCalculatorChanged &&
          renderRatesCalculatorSection(financialInfo)}
        {hasMarkupCalculatorChanged && renderMarkupCalculator(financialInfo)}
      </>
    );
  };

  if (
    !hasFinancialInformationChanged &&
    !hasRatesCalculatorChanged &&
    !hasMarkupCalculatorChanged
  )
    return null;

  return (
    <HistoryLog
      historyId={historyId}
      oldData={renderFinancialInformation(
        enhancedOldMultiRates,
        oldData,
        financialInfoOldHistory,
      )}
      newData={renderFinancialInformation(
        enhancedNewMultiRates,
        newData,
        financialInfoNewHistory,
      )}
    />
  );
}
